import { Fragment, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Grid } from '../grid/grid';
import { Button } from '../button/button.component';
import Svg from '../svg/svg.component';
import { useIsInEditorMode } from '../../utils/use-is-in-editor-mode';

import { LogosContentContainerProps } from './logos-content-container.config';
import { getComponentFromProps, getPropsFromModel } from '../../utils/use-child-components';
import { useDragComponentsPlaceholder } from '../../utils/use-drag-components-placeholder';

const DEFAULT_VISIBLE_ITEMS = 12;

const LogosContentContainer = ({
  accessibilityLabel,
  additionalClasses,
  cqItems,
  cqItemsOrder,
  cqPath,
  id,
  isInEditor,
  roleAttribute,
}: LogosContentContainerProps) => {
  const { t } = useTranslation();
  const { isInEditorMode } = useIsInEditorMode(isInEditor);
  const { placeholder } = useDragComponentsPlaceholder({ cqPath, isInEditor });

  const [allItemsVisible, setAllItemsVisible] = useState(false);

  const toggleShowMore = () => {
    setAllItemsVisible((prevState) => !prevState);
  };

  return (
    <div
      aria-label={accessibilityLabel}
      data-testid="snowflake-logos"
      className={classNames(
        'snowflake-logos snowflake-logo',
        `snowflake-logos-${cqItemsOrder.length}`,
        additionalClasses
      )}
      id={id}
      role={roleAttribute}
    >
      <Grid container justifyCenter className="snowflake-logos-grid-container">
        {cqItemsOrder
          .slice(0, allItemsVisible || isInEditorMode ? cqItemsOrder.length : DEFAULT_VISIBLE_ITEMS)
          .map((cqItem, i) => {
            const cqItemProps = cqItems[cqItem];
            const itemProps = getPropsFromModel(cqItemProps, cqPath, cqItem);
            const Item = getComponentFromProps(itemProps);

            return (
              <Fragment key={cqItem}>
                <Grid item mobile={6} tablet={3} smallDesktop={2} desktop={2}>
                  <div className="snowflake-logos-card-container" data-testid="snowflake-logos-card-container">
                    {Item && <Item {...itemProps} />}
                  </div>
                </Grid>
                <span className="snowflake-logos-line-break" />
              </Fragment>
            );
          })}
      </Grid>
      {cqItemsOrder.length > DEFAULT_VISIBLE_ITEMS && !isInEditorMode ? (
        <div className="snowflake-logos-show-more-button-container snowflake-button-compact snowflake-button-link snowflake-button-blue d-flex justify-center w-100">
          <Button
            text={!allItemsVisible ? t('show_more') : t('show_less')}
            disableAnchor
            onClick={toggleShowMore}
            endAdornment={
              <Svg
                name="arrow-down-icon"
                className={classNames('snowflake-logos-arrow', {
                  'snowflake-logos-arrow-open': allItemsVisible,
                })}
              />
            }
          />
        </div>
      ) : null}
      {placeholder}
    </div>
  );
};

export default LogosContentContainer;
